import { registerCloseSidePanelsListener } from '@databases/event-bus';
import { activityLogger } from '@databases/logs/activity-logger';
import { DatabaseField, DatabaseFieldType } from '@databases/types/database';
import React, {
  createContext,
  useState,
  PropsWithChildren,
  FC,
  useCallback,
  useEffect,
} from 'react';

type AllowedPanelTypes = {
  ViewSettingsPanel: {
    autoFocusLastFilterValue?: boolean;
    canEditViews: boolean;
  };
  FieldCreatePanel: {
    type: DatabaseFieldType;
    field: DatabaseField<DatabaseFieldType>;
    disableMulti?: boolean;
    enforceSelectOption?: boolean;
  };
  FieldSettingsPanel: {
    fieldId: string;
    createSelectOption?: boolean | undefined;
  };
  EntryViewPanel: {
    entryId: string;
    contentId?: string;
    noDelete?: boolean;
    isReadonly?: boolean;
  };
  DatabaseSetupPanel: undefined;
  CopyDatabasePanel: undefined;
};

type AllowedPanelKeys = keyof AllowedPanelTypes;

type PanelDataType = AllowedPanelTypes[AllowedPanelKeys];

export type PanelsType = {
  [key in AllowedPanelKeys]?: React.ReactNode;
};

export type PanelUnsavedChangesStateType = {
  [key in AllowedPanelKeys]: boolean;
};

export type ShowPanelType = <key extends AllowedPanelKeys>(
  panelKey: key,
  data?: AllowedPanelTypes[key]
) => void;

export interface PanelContextInterface {
  showPanel: ShowPanelType;
  hidePanel: () => void;
  getData: <T extends AllowedPanelKeys>() => AllowedPanelTypes[T];
  getPanelComponent: () => React.ReactNode;
  getPanelKey: () => AllowedPanelKeys | undefined;
  isPanelVisible: (panelKey?: AllowedPanelKeys) => boolean;
}

type PanelType = {
  panelKey: AllowedPanelKeys | undefined;
  component: React.ReactNode;
  data: PanelDataType;
};

export const PanelContext = createContext<PanelContextInterface>({
  showPanel: () => {},
  hidePanel: () => {},
  getData: <T,>() => null as T,
  getPanelComponent: () => null,
  getPanelKey: () => undefined,
  isPanelVisible: () => false,
});

export const PanelProvider: FC<
  PropsWithChildren<{
    defaultPanels: PanelsType;
  }>
> = ({ children, defaultPanels }) => {
  const [panels] = useState<PanelsType>(defaultPanels);
  const [panel, setPanel] = useState<PanelType>({
    component: null,
    data: undefined,
    panelKey: undefined,
  });

  const showPanel: ShowPanelType = useCallback(
    (panelKey, data) => {
      setPanel({ component: panels[panelKey], data, panelKey });
      activityLogger.log('showPanel', { panelKey: panel.panelKey, ...data });
    },
    [panel.panelKey, panels]
  );

  const hidePanel = useCallback(() => {
    setPanel({ component: null, data: undefined, panelKey: undefined });

    activityLogger.log('hidePanel', { panelKey: panel.panelKey });
  }, [panel.panelKey]);

  useEffect(() => {
    registerCloseSidePanelsListener(hidePanel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPanelVisible = useCallback(
    (panelKey?: AllowedPanelKeys) => {
      if (!panelKey) {
        return panel.component !== null;
      }

      if (panel.component === null) {
        return false;
      }

      return panel.panelKey === panelKey;
    },
    [panel.component, panel.panelKey]
  );

  const getData = useCallback(<T,>(): T => panel.data as T, [panel.data]);
  const getPanelComponent = useCallback(() => panel.component, [panel.component]);
  const getPanelKey = useCallback(() => panel.panelKey, [panel.panelKey]);

  return (
    <PanelContext.Provider
      value={{
        showPanel,
        hidePanel,
        getData,
        getPanelComponent,
        isPanelVisible,
        getPanelKey,
      }}
    >
      {children}
    </PanelContext.Provider>
  );
};
